import { useState, useEffect, useRef, useContext } from 'react';
import { useLocation } from "react-router-dom";
import Collapse from 'react-bootstrap/Collapse';
import useComponentVisible from '../hooks/useComponentVisible';
import Tooltip from './Tooltip';
import { AppContext } from '../util/AppContext';
import { getConfigValue } from '../util/getConfigValue';
import Mustache from 'mustache';
import { getSanitizedHtml } from '../util/getSanitizedHtml';
import { computeBorderColorClass, getIndicatorClass } from '../util/getBorderColor'
import PropType from 'prop-types';
import PrintRestrictionModal from './modals/PrintRestrictionModal';
import RejectClaimModal from './modals/RejectClaimModal';
import PlanListRowModal from './PlanListRowModal';
import PlanListColumn from './PlanListColumn';
import PropTypes from 'prop-types';

PlanListRow.propTypes = {
	plan: PropType.shape({
		columns: PropTypes.object,
		data: PropType.shape({
			name: PropType.string,
			benType: PropType.string,
			isPrintable: PropType.bool,
			planClass: PropType.string,
			isWin: PropType.bool,
			formularyRestriction: PropType.string,
			planId: PropType.string,
			formularyDrillDown: PropType.arrayOf(
				PropType.shape({
					formulary: PropType.string,
					coverage: PropType.string,
					livesPercentage: PropType.number,
				})
			),
			specialtyPharmacies: PropType.array,
			minPrintPlans: PropType.number
		}),
	}),
	toggleCollapse: PropType.bool,
	exceedMaxColumns: PropType.bool,
	tableColumnHeaders: PropType.array,
	selectedPlans: PropType.array,
	isSingleSelection: PropType.bool,
	index: PropType.number,
	isExpandAll: PropType.bool,
	isImprovedAccessStatic: PropType.bool,
	onOptionsClick: PropType.func,
	onClick: PropType.func,
	formularyDrillDownEnabled: PropType.bool,
	restrictionDrillDownEnabled: PropType.bool,
	specialtyPharmacyDrillDownEnabled: PropType.bool,
	canSelectFormulary: PropType.bool,
	canSelectRestrictions: PropType.bool,
	canSelectSpecialtyPharmacy: PropType.bool,
	columnsSpec: PropType.string,
	enableInlineIcons: PropType.bool,
	rejectClaimEnabled: PropType.bool,
};



function PlanListRow({
	plan,
	onClick,
	onOptionsClick,
	isImprovedAccessStatic,
	isExpandAll,
	index,
	isSingleSelection,
	selectedPlans,
	tableColumnHeaders,
	exceedMaxColumns,
	toggleCollapse,
	formularyDrillDownEnabled,
	restrictionDrillDownEnabled,
	specialtyPharmacyDrillDownEnabled,
	canSelectFormulary,
	canSelectRestrictions,
	canSelectSpecialtyPharmacy,
	columnsSpec,
	enableInlineIcons,
	rejectClaimEnabled
}) {
	const [appState] = useContext(AppContext);
	const [optionsMenuRef, optionsMenuIsVisible, setOptionsMenuIsVisible] = useComponentVisible(false);
	const { state } = useLocation();

	const [moreInfoMenuIsVisible, setMoreInfoMenuIsVisible] = useState(false);
	const [includeRestrictions, setIncludeRestrictions] = useState(false);
	const [includeFormularies, setIncludeFormularies] = useState(false);
	const [includeSpecialty, setIncludeSpecialty] = useState(false);
	const [includeCompetitiveRestrictionNotes, setIncludeCompetitiveRestrictionNotes] = useState(plan.data.includeCompetitiveRestrictionNotes || []);
	const [improvedAccessSelectedPlan, setImprovedAccessSelectedPlan] = useState(false);

	const enableEllipsisButton = getConfigValue(appState.config.PlanList.Options.EllipsisMenu, appState)?.Value === 'true';

	const isCurrentPlanSelected = selectedPlans.filter((e) => e.planId === plan.data.planId).length > 0;
	const showWarning =
		(plan.data.minPrintPlans &&
			parseInt(plan.data.minPrintPlans) > 0 &&
			isCurrentPlanSelected &&
			selectedPlans.filter((e) => e.planClass !== plan.data.planClass).length < parseInt(plan.data.minPrintPlans)) ||
		false;

	let warningLabel = getConfigValue(appState.config.Plan.SelectionMessage, appState)?.Value;
	warningLabel = Mustache.render(warningLabel, plan.data);

	const [warningVisible, setWarningVisible] = useState(false);

	const [showTooltip, setShowTooltip] = useState(false);
	const tooltipTarget = useRef(null);
	const [maxDisplayColumns, setMaxDisplayColumns] = useState(5);

	const [showPrintRestrictionModal, setShowPrintRestrictionModal] = useState(false);
	const [showRejectClaimModal, setShowRejectClaimModal] = useState(false);

	const { name, benType, isPrintable, planClass, isWin } = plan.data;

	useEffect(() => {
		setMoreInfoMenuIsVisible(isExpandAll);
	}, [isExpandAll, toggleCollapse]);

	useEffect(() => {
		if (appState?.config?.PlanList?.MaxDisplayColumns) {
			setMaxDisplayColumns(parseInt(getConfigValue(appState.config.PlanList.MaxDisplayColumns, appState)?.Value));
		}
	}, [appState]);

	useEffect(() => {
		const selected = selectedPlans.find((e) => e.planId === plan.data.planId);
		if (!selected) {
			setIncludeFormularies(false);
			setIncludeRestrictions(false)
			setIncludeSpecialty(false);
			setIncludeCompetitiveRestrictionNotes([]);
		} else {
			setIncludeFormularies(selected.includeFormularies || false);
			setIncludeRestrictions(selected.includeRestrictions || false);
			setIncludeSpecialty(selected.includeSpecialtyPharmacy || false);
			setIncludeCompetitiveRestrictionNotes(selected.includeCompetitiveRestrictionNotes || []);
		}
	}, [selectedPlans, plan.data.planId]);

	let isWarningRow = planClass !== null;
	let isDangerRow = !isPrintable;

	let borderColorClass = computeBorderColorClass(isWin, isPrintable, planClass);

	useEffect(() => {
		if (state?.planId) {
			setImprovedAccessSelectedPlan(plan.data.planId === state?.planId);
		} else {
			setImprovedAccessSelectedPlan(false);
		}
	}, [state?.planId, plan.data.planId])

	function canShowWarning() {
		if (isSingleSelection) {
			return warningVisible;
		}

		return showWarning;
	}

	function singleSelect(plan, event) {
		if (!isWarningRow) {
			onClick(plan.data, event);
			return null;
		} else {
			setWarningVisible(!warningVisible);
		}
	}

	const extendedRowDetailsButton = (index) => (
		<>


			<div className='d-flex flex-column'>
				<button
					className='btn btn-outline-primary btn-md btn-icon d-lg-none'
					onClick={() => setMoreInfoMenuIsVisible(!moreInfoMenuIsVisible)}
					data-bs-toggle='collapse'
					data-bs-target={`#datatable-row-${index}`}
					aria-expanded='false'
					aria-controls={`datatable-row-${index}`}>
					{getConfigValue(appState.config?.PlanList?.Options?.MoreInfo.Label, appState)?.Value}{' '}
					<span className='fa-regular fa-chevron-down btn-icon'></span>
				</button>
			</div>
			{exceedMaxColumns && (
				<button
					className='btn btn-ghost-secondary btn-sm btn-icon-only d-none d-lg-flex'
					onClick={() => setMoreInfoMenuIsVisible(!moreInfoMenuIsVisible)}
					data-bs-toggle='collapse'
					data-bs-target={`#datatable-row-${index}`}
					aria-expanded='false'
					aria-controls={`datatable-row-${index}`}>
					<span className='fa-regular fa-chevron-down btn-icon'></span>
				</button>
			)}
		</>
	)

	const ellipsisButton = (
		<>
			{!isDangerRow && enableEllipsisButton && (
				<PlanListRowModal
					plan={plan}
					formularyDrillDownEnabled={formularyDrillDownEnabled}
					specialtyPharmacyDrillDownEnabled={specialtyPharmacyDrillDownEnabled}
					restrictionDrillDownEnabled={restrictionDrillDownEnabled}
					isTopLevelItem={true}
					index={index}
					onOptionsClick={onOptionsClick}
					canSelectFormulary={canSelectFormulary}
					canSelectRestrictions={canSelectRestrictions}
					canSelectSpecialtyPharmacy={canSelectSpecialtyPharmacy}
					includeFormularies={includeFormularies}
					setIncludeFormularies={setIncludeFormularies}
					includeSpecialty={includeSpecialty}
					setIncludeSpecialty={setIncludeSpecialty}
					includeRestrictions={includeRestrictions}
					setIncludeRestrictions={setIncludeRestrictions}
					includeCompetitiveRestrictionNotes={includeCompetitiveRestrictionNotes}
					setIncludeCompetitiveRestrictionNotes={setIncludeCompetitiveRestrictionNotes}
					enableEllipsisButton={enableEllipsisButton}
					enableInlineIcons={false}
				></PlanListRowModal>
			)}
		</>
	)
	const extendedColumns = (
		<>
			{(enableEllipsisButton || exceedMaxColumns) && (
				<>
					{exceedMaxColumns ? (
						<div className=' d-flex justify-content-end plan-column-options' >
							{ellipsisButton}

							{/* <!-- Button - more info --> */}
							{extendedRowDetailsButton(index)}
						</div>
					) : (
						<div className='d-flex justify-content-end plan-column-options'>
							{ellipsisButton}
						</div>
					)}
				</>
			)}
		</>
	);

	const columns = (
		<>
			{Object.entries(plan.columns).map(([columnId, value], columnIndex) => {
				return (
					columnIndex < maxDisplayColumns && (
						value.includes('data-product-id') ?
							<div key={columnId} className='mb-3 my-lg-0 plan-column-options' style={{ color: optionsMenuIsVisible ? 'cadetblue' : 'inherit' }}>
								<PlanListRowModal
									plan={plan}
									value={value}
									formularyDrillDownEnabled={formularyDrillDownEnabled}
									specialtyPharmacyDrillDownEnabled={specialtyPharmacyDrillDownEnabled}
									restrictionDrillDownEnabled={restrictionDrillDownEnabled}
									isTopLevelItem={true}
									index={index}
									onOptionsClick={onOptionsClick}
									canSelectFormulary={canSelectFormulary}
									canSelectRestrictions={canSelectRestrictions}
									canSelectSpecialtyPharmacy={canSelectSpecialtyPharmacy}
									includeFormularies={includeFormularies}
									setIncludeFormularies={setIncludeFormularies}
									includeSpecialty={includeSpecialty}
									setIncludeSpecialty={setIncludeSpecialty}
									includeRestrictions={includeRestrictions}
									setIncludeRestrictions={setIncludeRestrictions}
									includeCompetitiveRestrictionNotes={includeCompetitiveRestrictionNotes}
									setIncludeCompetitiveRestrictionNotes={setIncludeCompetitiveRestrictionNotes}
									enableInlineIcons={enableInlineIcons}
								></PlanListRowModal>
							</div>
							:
							<PlanListColumn 
									key={columnId} 
									index={index} 
									value={value} 
									plan={plan}
									formularyDrillDownEnabled={formularyDrillDownEnabled}
									specialtyPharmacyDrillDownEnabled={specialtyPharmacyDrillDownEnabled}
									restrictionDrillDownEnabled={restrictionDrillDownEnabled}
									onOptionsClick={onOptionsClick}
									canSelectFormulary={canSelectFormulary}
									canSelectRestrictions={canSelectRestrictions}
									canSelectSpecialtyPharmacy={canSelectSpecialtyPharmacy}
									includeFormularies={includeFormularies}
									setIncludeFormularies={setIncludeFormularies}
									includeSpecialty={includeSpecialty}
									setIncludeSpecialty={setIncludeSpecialty}
									includeRestrictions={includeRestrictions}
									setIncludeRestrictions={setIncludeRestrictions}
									enableInlineIcons={enableInlineIcons}
							/>
					)
				);
			})}
		</>
	)
	const columnsLayout = (
		<div className='pd-plan-col pd-plan-variable col-12 col-lg-11'>
			<div className='align-items-lg-center' style={{ display: 'grid', gridTemplateColumns: `${columnsSpec}` }}>
				{columns}

				{rejectClaimEnabled && (
					<div className='mb-3 my-lg-0 d-flex align-items-center'>
						<button className='reject-claim-details-btn' onClick={() => setShowRejectClaimModal(true)}>
							{getConfigValue(appState.config?.RejectClaim?.Modal?.Label, appState)?.Value || 'Reject Claim Details'}
							<span className='fa-regular fa-chevron-right'></span>
						</button>
					</div>
				)}

				{/* <!-- Ellipsis button and or extra column details button --> */}
				{extendedColumns}
			</div>
		</div>
	)

	return (
		<div className={`row pd-plan-table-row ${borderColorClass ? getIndicatorClass(borderColorClass) : ''
			} ${improvedAccessSelectedPlan ? "color-bg-warning-15" : ""} pd-plan-table-card mb-6 mb-lg-0 pt-3 pt-lg-0 justify-content-lg-between align-items-lg-center`}>
			<div className='pd-plan-col col-12 col-lg-1 px-0 py-lg-3'>
				<div className='pd-plan-table-col pd-plan-table-input-main'>
					{isDangerRow ? (
						<>
							<span
								ref={tooltipTarget}
								onMouseOver={() => setShowTooltip(true)}
								onFocus={() => setShowTooltip(true)}
								onMouseOut={() => setShowTooltip(false)}
								onBlur={() => setShowTooltip(false)}
								onClick={e => setShowTooltip(!showTooltip)}
								role="button"
								className='far fa-print-slash type-20 color-danger'
								id={`${benType}${name}`}></span>
							<Tooltip
								text={`${getConfigValue(appState.config?.PlanList?.PrintRestrictionTooltip, appState)?.Value}`}
								ref={tooltipTarget}
								showTooltip={showTooltip}
								index={index}
							/>
						</>
					) : (
						<div>
							{planClass && isSingleSelection ?
								<button className="far fa-print-slash type-20 color-grey-dark-1 btn-reset"
									onClick={() => setShowPrintRestrictionModal(true)}>
								</button>
								:
								//   <!-- DEV NOTES Need to alternate between different types of checkboxes and radio button. If they are in static Improved Access use radio button else checkbox && show only winPlans --> */
								<div className='form-check'>
									{isSingleSelection ? (
										// if from static improved access or is single selection, use radio button:
										<input
											className='form-check-input'
											onChange={(event) => singleSelect(plan, event)}
											type='radio'
											name='planlist-radio-group'
											checked={isCurrentPlanSelected}
											id={`planlist-input-check-row${index}`}
											data-plan-alert={`planlist-plan-alert-row${index}`}
										/>
									) : (
										// else use checkbox:
										<input
											className='form-check-input'
											onChange={(event) => onClick(plan.data, event)}
											type='checkbox'
											checked={improvedAccessSelectedPlan || isCurrentPlanSelected}
											disabled={improvedAccessSelectedPlan}
											id={`planlist-input-check-row${index}`}
											data-plan-alert={`planlist-plan-alert-row${index}`}
										/>
									)}

									{/* <!-- DEV NOTES Note the text strings of Channel + Plan in the <label> element --> */}
									<label className='form-check-label visually-hidden' htmlFor={`planlist-input-check-row${index}`}>
										Select {benType} - {name}
									</label>
								</div>
							}
						</div>
					)}
				</div>
			</div>

			{/* <!-- Plan alert --> */}
			<div
				className='pd-plan-col pd-plan-alert col-12 order-lg-last collapse'
				style={{ display: canShowWarning() ? 'flex' : 'none' }}
				id={`planlist-plan-alert-row${index}`}>
				<span className='fas fa-info-circle'></span>
				<p className='my-0 ms-3'>{warningLabel}</p>
			</div>

			{columnsLayout}

			{/*
				<div className='pd-plan-col col-12 col-lg-1 mt-4 mb-2 my-lg-0'>
				</div>
			*/}

			{/* <!-- Data table --> */}
			{exceedMaxColumns && (
				<Collapse in={moreInfoMenuIsVisible}>
					<div className='pd-plan-col pd-plan-datatable col-12 order-lg-last px-0' id={`datatable-row-${index}`}>
						<div className='pd-plan-datatable'>
							<div className='pd-plan-datatable-row'>
								<div className='pd-plan-datatable-col pd-datatable-label'>
									{tableColumnHeaders.map((header) => {
										return <div key={header.key} className='col-lg' dangerouslySetInnerHTML={getSanitizedHtml(header.title)}></div>;
									})}
								</div>
								<div className='pd-plan-datatable-col pd-datatable-value'>
									{Object.entries(plan.columns).map(([key, value], columnIndex) => {
										return (
											value.includes('data-product-id') ?
												<div key={key} className='col-6 col-lg mb-3 my-lg-0 plan-column-options' style={{ color: optionsMenuIsVisible ? 'cadetblue' : 'inherit' }}>
													<PlanListRowModal
														plan={plan}
														value={value}
														formularyDrillDownEnabled={formularyDrillDownEnabled}
														specialtyPharmacyDrillDownEnabled={specialtyPharmacyDrillDownEnabled}
														restrictionDrillDownEnabled={restrictionDrillDownEnabled}
														index={index}
														onOptionsClick={onOptionsClick}
														canSelectFormulary={canSelectFormulary}
														canSelectRestrictions={canSelectRestrictions}
														canSelectSpecialtyPharmacy={canSelectSpecialtyPharmacy}
														includeFormularies={includeFormularies}
														setIncludeFormularies={setIncludeFormularies}
														includeSpecialty={includeSpecialty}
														setIncludeSpecialty={setIncludeSpecialty}
														includeRestrictions={includeRestrictions}
														setIncludeRestrictions={setIncludeRestrictions}
														includeCompetitiveRestrictionNotes={includeCompetitiveRestrictionNotes}
														setIncludeCompetitiveRestrictionNotes={setIncludeCompetitiveRestrictionNotes}
														enableInlineIcons={enableInlineIcons}
													>
													</PlanListRowModal>
												</div>
												:
												<PlanListColumn 
														key={key} 
														index={index} 
														value={value} 
														plan={plan}
														formularyDrillDownEnabled={formularyDrillDownEnabled}
														specialtyPharmacyDrillDownEnabled={specialtyPharmacyDrillDownEnabled}
														restrictionDrillDownEnabled={restrictionDrillDownEnabled}
														onOptionsClick={onOptionsClick}
														canSelectFormulary={canSelectFormulary}
														canSelectRestrictions={canSelectRestrictions}
														canSelectSpecialtyPharmacy={canSelectSpecialtyPharmacy}
														includeFormularies={includeFormularies}
														setIncludeFormularies={setIncludeFormularies}
														includeSpecialty={includeSpecialty}
														setIncludeSpecialty={setIncludeSpecialty}
														includeRestrictions={includeRestrictions}
														setIncludeRestrictions={setIncludeRestrictions}
														enableInlineIcons={enableInlineIcons}
													/>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</Collapse>
			)}

			<PrintRestrictionModal show={showPrintRestrictionModal} setShow={setShowPrintRestrictionModal} />
			<RejectClaimModal 
				show={showRejectClaimModal} 
				setShow={setShowRejectClaimModal} 
				plan={plan}
				/>
		</div>
	);
}

export default PlanListRow;
